import * as React from "react";
import Tooltip from "@mui/material/Tooltip";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import classStyle from "./style.module.scss";

// Clean, static-bar metric card — redesigned so the peer marker on the
// track actually means something at a glance: the exact peer figure now
// sits anchored directly under the pin instead of floating, unlabeled,
// disconnected from the bar. The header carries the at-a-glance verdict
// ("75% Less than Peers") instead of a lone unexplained info icon.

const formatValue = (value, isCost) => {
  const num = Number(value) || 0;
  const rounded = Math.round((num + Number.EPSILON) * 10) / 10;
  const display = Number.isInteger(rounded) ? rounded : rounded.toFixed(1);
  return isCost ? `$${display}` : `${display}%`;
};

const DiscreteSlider = (props) => {
  const { item } = props;
  const {
    doctorValue,
    peerAvgValue,
    key,
    peerComparisionDescription,
    toolTipNumerator,
    toolTipDenominator,
  } = item || {};

  const isCost = (key || "").toLowerCase().includes("cost");
  const doctor = Number(doctorValue) || 0;
  const peer = Number(peerAvgValue) || 0;
  const isSame = doctor === peer;

  // Same scale derivation as before: headroom above the larger of the
  // two values so neither the fill nor the marker sits flush against
  // the track edge.
  const scale = isSame ? 1 : Math.max(doctor, peer) * 1.15 || 1;
  const fillPercent = isSame ? 50 : Math.min(100, (doctor / scale) * 100);
  const markerPercent = isSame ? 50 : Math.min(100, (peer / scale) * 100);

  // A pure percentage clamp isn't reliable across card widths (these
  // cards can run anywhere from ~220px to much wider in the grid) —
  // a fixed pixel-width label like "Peers $452" needs a bigger safety
  // margin on a narrow card than a wide one. So instead of just
  // clamping position, the label's anchor point flips near the edges:
  // centered under the pin in the middle of the track, but pinned flush
  // to the card's own edge (never past it) once the pin gets close.
  let labelAlign = "center";
  if (markerPercent < 20) labelAlign = "left";
  else if (markerPercent > 80) labelAlign = "right";

  // Same as peers reuses the "good" (green) status rather than a
  // separate neutral/gray one — matching peer performance isn't a
  // red flag, so it reads as a positive/reassuring result, not a
  // shrug. Worse-than-peer still maps to "bad" (red) below, unchanged.
  const status = isSame ? "good" : doctor < peer ? "good" : "bad";

  // Full sentence for the tooltip/title (hover for detail) — but the
  // always-visible badge stays compact so it never has to fight the
  // label for space on a fixed-width card. "than Peers" is redundant
  // here anyway: the whole card is already a peer comparison, so it
  // doesn't need repeating in every single badge.
  const compareTextFull = isSame
    ? "Same as peers"
    : peerComparisionDescription ||
      (doctor < peer ? "Less than peers" : "More than peers");

  const diffPercent =
    peer > 0
      ? Math.round((Math.abs(doctor - peer) / peer) * 100)
      : doctor > 0
      ? 100
      : 0;

  const compareTextCompact = isSame
    ? "Same"
    : `${diffPercent}% ${doctor < peer ? "Less" : "More"}`;

  return (
    <div className={classStyle["slider-wrapper"]}>
      <div className={classStyle["mcard-header"]}>
        <span className={classStyle["mcard-label"]}>
          {key}
          <Tooltip
            title={
              <div>
                <p className={classStyle["tooltip-text"]}>Num: {toolTipNumerator}</p>
                <p className={classStyle["tooltip-text"]}>Den: {toolTipDenominator}</p>
              </div>
            }
            componentsProps={{
              tooltip: {
                sx: {
                  color: "black",
                  backgroundColor: "#fff",
                  fontSize: "14px",
                  border: "1px solid #eee",
                  boxShadow: "1px 2px 3px #555",
                  fontWeight: "bolder",
                },
              },
            }}
          >
            <ErrorOutlineRoundedIcon className={classStyle["mcard-info-icon"]} />
          </Tooltip>
        </span>
        <span
          className={`${classStyle["mcard-compare-badge"]} ${classStyle[status]}`}
          title={compareTextFull}
        >
          <span className={classStyle["mcard-compare-dot"]}></span>
          {compareTextCompact}
        </span>
      </div>

      <div className={classStyle["mcard-hero"]}>
        <span className={classStyle["mcard-hero-eyebrow"]}>This provider</span>
        <span className={classStyle["mcard-value"]}>{formatValue(doctor, isCost)}</span>
      </div>

      <div className={classStyle["slider-zone"]}>
        <div className={classStyle["slider-track"]}>
          <div
            className={`${classStyle["slider-fill"]} ${classStyle[status]}`}
            style={{ width: `${fillPercent}%` }}
          ></div>
          <div
            className={classStyle["slider-marker"]}
            style={{ left: `${markerPercent}%` }}
            title={`Peer average: ${formatValue(peer, isCost)}`}
          ></div>
        </div>
        <span className={classStyle["peer-marker-tick"]} style={{ left: `${markerPercent}%` }}></span>
        <div
          className={classStyle["peer-marker-label"]}
          data-align={labelAlign}
          style={
            labelAlign === "left"
              ? { left: 0 }
              : labelAlign === "right"
              ? { right: 0 }
              : { left: `${markerPercent}%` }
          }
        >
          Peers {formatValue(peer, isCost)}
        </div>
      </div>
    </div>
  );
};

export default DiscreteSlider;